import {
  networkError,
  requestJson,
  toError,
  type Fetch,
  type HeaderSource,
  type QubBaseError,
} from "./http";

export interface Bucket {
  id: string;
  name: string;
  owner: string | null;
  public: boolean;
  file_size_limit: number | null;
  allowed_mime_types: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface FileObject {
  name: string;
  id: string | null;
  updated_at: string | null;
  created_at: string | null;
  last_accessed_at: string | null;
  metadata: {
    size?: number;
    mimetype?: string;
    cacheControl?: string;
    eTag?: string;
  } | null;
}

export type FileBody =
  | Blob
  | ArrayBuffer
  | ArrayBufferView
  | string
  | ReadableStream<Uint8Array>;

const encodePath = (p: string) =>
  p.replace(/^\/+/, "").split("/").map(encodeURIComponent).join("/");

/** One bucket: upload, download, list, move, copy, remove, signed and public URLs. */
export class StorageFileApi {
  constructor(
    private readonly url: string,
    private readonly bucketId: string,
    private readonly headers: HeaderSource,
    private readonly fetcher: Fetch,
  ) {}

  private json<T>(path: string, init: RequestInit) {
    return requestJson<T>(
      this.fetcher,
      `${this.url}${path}`,
      {
        ...init,
        headers: {
          "Content-Type": "application/json",
          ...(init.headers as Record<string, string>),
        },
      },
      this.headers,
    );
  }

  private async send(
    method: "POST" | "PUT",
    url: string,
    body: FileBody,
    options: { contentType?: string; cacheControl?: string; upsert?: boolean },
  ) {
    try {
      const contentType =
        options.contentType ??
        (body instanceof Blob && body.type
          ? body.type
          : typeof body === "string"
            ? "text/plain;charset=UTF-8"
            : "application/octet-stream");
      const init: RequestInit & { duplex?: "half" } = {
        method,
        headers: {
          ...(await this.headers()),
          "Content-Type": contentType,
          "Cache-Control": options.cacheControl ?? "3600",
          "x-upsert": String(Boolean(options.upsert)),
        },
        body: body as BodyInit,
      };
      if (
        typeof ReadableStream !== "undefined" &&
        body instanceof ReadableStream
      )
        init.duplex = "half";
      const res = await this.fetcher(url, init);
      if (!res.ok) return { data: null, error: await toError(res) };
      const data = (await res.json()) as {
        id?: string;
        Id?: string;
        Key: string;
        path: string;
      };
      return {
        data: {
          id: data.id ?? data.Id ?? null,
          path: data.path,
          fullPath: data.Key,
        },
        error: null,
      };
    } catch (err) {
      return { data: null, error: networkError(err) };
    }
  }

  upload(
    path: string,
    body: FileBody,
    options: {
      contentType?: string;
      cacheControl?: string;
      upsert?: boolean;
    } = {},
  ) {
    return this.send(
      "POST",
      `${this.url}/object/${this.bucketId}/${encodePath(path)}`,
      body,
      options,
    );
  }

  update(
    path: string,
    body: FileBody,
    options: { contentType?: string; cacheControl?: string } = {},
  ) {
    return this.send(
      "PUT",
      `${this.url}/object/${this.bucketId}/${encodePath(path)}`,
      body,
      { ...options, upsert: true },
    );
  }

  async download(
    path: string,
  ): Promise<{ data: Blob | null; error: QubBaseError | null }> {
    try {
      const res = await this.fetcher(
        `${this.url}/object/${this.bucketId}/${encodePath(path)}`,
        { headers: await this.headers() },
      );
      if (!res.ok) return { data: null, error: await toError(res) };
      return { data: await res.blob(), error: null };
    } catch (err) {
      return { data: null, error: networkError(err) };
    }
  }

  async info(path: string) {
    return this.json<{
      id: string;
      name: string;
      bucket_id: string;
      owner: string | null;
      metadata: FileObject["metadata"];
      created_at: string;
      updated_at: string;
    }>(`/object/info/${this.bucketId}/${encodePath(path)}`, { method: "GET" });
  }

  async exists(path: string) {
    try {
      const res = await this.fetcher(
        `${this.url}/object/${this.bucketId}/${encodePath(path)}`,
        { method: "HEAD", headers: await this.headers() },
      );
      return { data: res.ok, error: null };
    } catch (err) {
      return { data: false, error: networkError(err) };
    }
  }

  list(
    prefix = "",
    options: {
      limit?: number;
      offset?: number;
      search?: string;
      sortBy?: { column: string; order: "asc" | "desc" };
    } = {},
  ) {
    return this.json<FileObject[]>(`/object/list/${this.bucketId}`, {
      method: "POST",
      body: JSON.stringify({ prefix, ...options }),
    });
  }

  move(
    fromPath: string,
    toPath: string,
    options: { destinationBucket?: string } = {},
  ) {
    return this.json<{ message: string }>("/object/move", {
      method: "POST",
      body: JSON.stringify({
        bucketId: this.bucketId,
        sourceKey: fromPath,
        destinationKey: toPath,
        destinationBucket: options.destinationBucket,
      }),
    });
  }

  async copy(
    fromPath: string,
    toPath: string,
    options: { destinationBucket?: string } = {},
  ) {
    const { data, error } = await this.json<{ Key: string }>("/object/copy", {
      method: "POST",
      body: JSON.stringify({
        bucketId: this.bucketId,
        sourceKey: fromPath,
        destinationKey: toPath,
        destinationBucket: options.destinationBucket,
      }),
    });
    return { data: data ? { path: data.Key } : null, error };
  }

  remove(paths: string[]) {
    return this.json<FileObject[]>(`/object/${this.bucketId}`, {
      method: "DELETE",
      body: JSON.stringify({ prefixes: paths }),
    });
  }

  async createSignedUrl(
    path: string,
    expiresIn: number,
    options: { download?: string | boolean } = {},
  ) {
    const { data, error } = await this.json<{ signedURL: string }>(
      `/object/sign/${this.bucketId}/${encodePath(path)}`,
      { method: "POST", body: JSON.stringify({ expiresIn }) },
    );
    if (!data) return { data: null, error };
    const dl = options.download
      ? `&download=${options.download === true ? "" : encodeURIComponent(options.download)}`
      : "";
    return {
      data: { signedUrl: `${this.url}${data.signedURL}${dl}` },
      error: null,
    };
  }

  async createSignedUrls(paths: string[], expiresIn: number) {
    const { data, error } = await this.json<
      { path: string; signedURL: string | null; error: string | null }[]
    >(`/object/sign/${this.bucketId}`, {
      method: "POST",
      body: JSON.stringify({ expiresIn, paths }),
    });
    return {
      data:
        data?.map((d) => ({
          path: d.path,
          error: d.error,
          signedUrl: d.signedURL ? `${this.url}${d.signedURL}` : null,
        })) ?? null,
      error,
    };
  }

  async createSignedUploadUrl(
    path: string,
    options: { upsert?: boolean } = {},
  ) {
    const { data, error } = await this.json<{
      url: string;
      token: string;
      path: string;
    }>(`/object/upload/sign/${this.bucketId}/${encodePath(path)}`, {
      method: "POST",
      headers: options.upsert ? { "x-upsert": "true" } : {},
    });
    return {
      data: data
        ? {
            signedUrl: `${this.url}${data.url}`,
            token: data.token,
            path: data.path,
          }
        : null,
      error,
    };
  }

  uploadToSignedUrl(
    path: string,
    token: string,
    body: FileBody,
    options: { contentType?: string; cacheControl?: string } = {},
  ) {
    return this.send(
      "PUT",
      `${this.url}/object/upload/sign/${this.bucketId}/${encodePath(path)}?token=${encodeURIComponent(token)}`,
      body,
      options,
    );
  }

  /** URL for objects in public buckets (no request is made). */
  getPublicUrl(path: string, options: { download?: string | boolean } = {}) {
    const dl = options.download
      ? `?download=${options.download === true ? "" : encodeURIComponent(options.download)}`
      : "";
    return {
      data: {
        publicUrl: `${this.url}/object/public/${this.bucketId}/${encodePath(path)}${dl}`,
      },
    };
  }
}

/** `client.storage` — buckets (service role) and per-bucket file APIs. */
export class StorageClient {
  constructor(
    private readonly url: string,
    private readonly headers: HeaderSource,
    private readonly fetcher: Fetch,
  ) {}

  from(bucketId: string) {
    return new StorageFileApi(this.url, bucketId, this.headers, this.fetcher);
  }

  private json<T>(path: string, init: RequestInit) {
    return requestJson<T>(
      this.fetcher,
      `${this.url}${path}`,
      { ...init, headers: { "Content-Type": "application/json" } },
      this.headers,
    );
  }

  listBuckets() {
    return this.json<Bucket[]>("/bucket", { method: "GET" });
  }
  getBucket(id: string) {
    return this.json<Bucket>(`/bucket/${id}`, { method: "GET" });
  }
  createBucket(
    id: string,
    options: {
      public?: boolean;
      fileSizeLimit?: number | string | null;
      allowedMimeTypes?: string[] | null;
    } = {},
  ) {
    return this.json<{ name: string }>("/bucket", {
      method: "POST",
      body: JSON.stringify({
        id,
        name: id,
        public: options.public ?? false,
        file_size_limit: options.fileSizeLimit ?? null,
        allowed_mime_types: options.allowedMimeTypes ?? null,
      }),
    });
  }
  updateBucket(
    id: string,
    options: {
      public?: boolean;
      fileSizeLimit?: number | string | null;
      allowedMimeTypes?: string[] | null;
    },
  ) {
    return this.json<{ message: string }>(`/bucket/${id}`, {
      method: "PUT",
      body: JSON.stringify({
        public: options.public,
        file_size_limit: options.fileSizeLimit,
        allowed_mime_types: options.allowedMimeTypes,
      }),
    });
  }
  emptyBucket(id: string) {
    return this.json<{ message: string }>(`/bucket/${id}/empty`, {
      method: "POST",
    });
  }
  deleteBucket(id: string) {
    return this.json<{ message: string }>(`/bucket/${id}`, {
      method: "DELETE",
    });
  }
}
